/**
 * Centralized React Query client configuration
 * 
 * ============================================================================
 * GLOBAL CACHING STRATEGY FOR CRM
 * ============================================================================
 * 
 * This file exports a pre-configured QueryClient with optimized defaults
 * for different data types (static, semi-static, realtime, etc.)
 * 
 * The caching strategy is designed to provide:
 * - Fast initial load after login/auto-login (header and layout appear immediately)
 * - Smart caching per page type (heavy vs. light)
 * - Real-time-ish header counters without spamming the backend
 * - Safe, explicit React Query configuration per domain
 * 
 * PROFESSIONAL CRM TUNING:
 * - Heavy tables (clients, leads, tasks): 2min stale, 60min cache, no window focus refetch
 * - Dashboards: 30s stale, 30min cache, refetch on window focus
 * - Config/static: 30min stale, 4h cache, minimal refetching
 * - Real-time widgets: 5s stale, 10min cache, refetch on focus + interval
 * - Detail pages: 1min stale, 30min cache, refetch on window focus
 * 
 * DEFAULT QUERY OPTIONS:
 * - staleTime: 0 (always considered stale by default, overridden per-query)
 * - gcTime: 5 minutes (keep in memory for 5 minutes)
 * - refetchOnMount: true (always refetch on mount for fresh data after login)
 * - refetchOnWindowFocus: false (selectively enabled where needed)
 * - refetchOnReconnect: true (always refetch on reconnect)
 * 
 * NOTE: keepPreviousData is not available in default options (React Query v5).
 * Use placeholderData: keepPreviousData in individual queries for smooth pagination.
 * 
 * QUERY OPTIONS PRESETS:
 * Use these presets across all query hooks to ensure consistent caching behavior:
 * 
 * 1. heavyTable - For heavy list pages (clients, leads, tasks, orders, inventory, contracts)
 *    - staleTime: 2 minutes
 *    - gcTime: 60 minutes
 *    - refetchOnWindowFocus: false
 *    - placeholderData: keepPreviousData (smooth pagination - set per-query)
 * 
 * 2. dashboard - For dashboard/overview pages
 *    - staleTime: 30 seconds
 *    - gcTime: 30 minutes
 *    - refetchOnWindowFocus: true
 * 
 * 3. config - For settings, permissions, pipelines, statuses, automations
 *    - staleTime: 30 minutes
 *    - gcTime: 4 hours
 *    - refetchOnWindowFocus: false
 * 
 * 4. realtimeWidget - For header counters, notification badges
 *    - staleTime: 5 seconds
 *    - gcTime: 10 minutes
 *    - refetchOnWindowFocus: true
 *    - refetchInterval: 15 seconds (if safe for backend)
 * 
 * 5. detailPage - For detail pages (client profile, lead detail, task detail)
 *    - staleTime: 1 minute
 *    - gcTime: 30 minutes
 *    - refetchOnWindowFocus: true
 * 
 * USAGE:
 * ```tsx
 * import { queryOptions } from '../lib/queryClient';
 * 
 * const { data } = useQuery({
 *   queryKey: ['clients', company],
 *   queryFn: fetchClients,
 *   ...queryOptions.heavyTable, // Use preset
 * });
 * ```
 * 
 * BOOTSTRAP ARCHITECTURE:
 * - useAppBootstrap: Loads core identity (user, company, permissions, features, plan)
 *   Called at Layout level for fast initial render
 * - useHeaderBootstrap: Loads header-specific data (user info, company logo)
 *   Called in Header component
 * - useHeaderCounts: Loads dynamic header counters (notifications, tasks, leads)
 *   Polls every 15 seconds, updates on window focus
 * 
 * CACHE PERSISTENCE:
 * - Query cache is persisted to localStorage via PersistedQueryClientProvider
 * - Persisted queries: auth, company, home, settings, clients, leads, tasks, orders, managers, agents, dashboard, contracts
 * - NOT persisted: notifications (too dynamic), analytics (can be regenerated)
 * - Cache expires after 24 hours
 * 
 * ============================================================================
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Create a new QueryClient with safe CRM defaults
 * 
 * Defaults are conservative and will be overridden per-page/feature
 */
/**
 * GLOBAL REACT QUERY DEFAULTS
 * 
 * These defaults prevent "revert after seconds" issues:
 * - refetchOnWindowFocus: false (prevents noisy refetches that overwrite UI)
 * - staleTime: 20 seconds (data is fresh for 20s, reduces unnecessary refetches)
 * - retry: 1 (fail fast, don't retry too many times)
 * 
 * Per-query options can override these defaults using queryOptions presets.
 */
// Helper to check if error is 401 (do not retry on 401)
function isAxios401Error(error: any): boolean {
  if (!error) return false;
  // Check if it's an axios error with 401 status
  if (error.response?.status === 401) return true;
  // Check if it's a session revoked error
  if (error.isSessionRevoked === true) return true;
  // Check if error message indicates auth failure
  if (error.message === 'AUTH_BLOCKED') return true;
  return false;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // CRITICAL: Never retry on 401 errors (auth failures)
        if (isAxios401Error(error)) {
          return false;
        }
        // Retry once for other errors
        return failureCount < 1;
      },
      refetchOnWindowFocus: false, // CRITICAL: Prevents noisy refetches that overwrite optimistic updates
      refetchOnReconnect: true, // Always refetch on reconnect (user expects fresh data)
      refetchOnMount: true, // Always refetch on mount (ensures fresh data after login/auto-login)
      staleTime: 20 * 1000, // 20 seconds - data is fresh for 20s (prevents unnecessary refetches)
      gcTime: 5 * 60 * 1000, // 5 minutes in memory (React Query v5 uses gcTime, not cacheTime)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    },
    mutations: {
      retry: (failureCount, error) => {
        // CRITICAL: Never retry on 401 errors (auth failures)
        if (isAxios401Error(error)) {
          return false;
        }
        // Retry once for other errors
        return failureCount < 1;
      },
      retryDelay: 1000,
    },
  },
});

/**
 * Professional query options presets for different data types
 * 
 * These constants should be used across all query hooks to ensure
 * consistent caching behavior per feature type.
 */
export const queryOptions = {
  /**
   * HEAVY TABLE PAGES (clients, leads, tasks, managers, agents, orders, inventory, contracts)
   * 
   * Goal: Good caching, but not stale for too long. User can switch pages and filters
   * without refetching everything all the time.
   * 
   * - staleTime: 2 minutes (data is fresh for 2min)
   * - gcTime: 60 minutes (keep in memory for 1 hour)
   * - refetchOnWindowFocus: false (don't hammer backend on tab switch)
   * - refetchOnMount: 'ifStale' (only refetch if data is older than staleTime)
   * - keepPreviousData: true (smooth pagination experience)
   */
  heavyTable: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes in memory
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    // refetchOnMount: true with staleTime set will only refetch if data is stale (older than staleTime)
    // This is the default behavior in React Query v5 when staleTime is set
    refetchOnMount: true,
    // NOTE: For smooth pagination, add placeholderData: keepPreviousData in individual queries
    // keepPreviousData is not available in query options presets (React Query v5)
  } as const,

  /**
   * MEDIUM DYNAMIC DASHBOARDS (home overview, dashboard KPIs)
   * 
   * Goal: KPIs and numbers are fresh, but without hammering the backend.
   * 
   * - staleTime: 30 seconds (data is fresh for 30s)
   * - gcTime: 30 minutes (keep in memory for 30min)
   * - refetchOnWindowFocus: true (user expects dashboard to refresh when returning)
   * - refetchOnMount: 'ifStale' (only refetch if stale)
   */
  dashboard: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: true, // User expects dashboard to refresh when returning
    refetchOnReconnect: true,
    refetchOnMount: true, // Refetch on mount, but only if data is stale (older than staleTime) - this is the default behavior in v5
  } as const,

  /**
   * ALMOST-STATIC CONFIG PAGES (settings, permissions, pipelines, statuses, automations, home catalog/prefs)
   * 
   * Goal: Rarely changes, so we want long staleTime and minimal refetching.
   * This reduces backend load massively.
   * 
   * - staleTime: 30 minutes (data is fresh for 30min)
   * - gcTime: 4 hours (keep in memory for 4 hours)
   * - refetchOnWindowFocus: false (don't refetch on tab switch)
   * - refetchOnMount: 'ifStale' (only refetch if stale)
   * 
   * NOTE: If user edits a setting on the same page, make sure to invalidate
   * the relevant query with queryClient.invalidateQueries so the long staleTime
   * does NOT freeze outdated data.
   */
  config: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: true, // Refetch on mount, but only if data is stale (older than staleTime) - this is the default behavior in v5
  } as const,

  /**
   * MEDIUM LISTS (Managers, Agents, Pipelines, Statuses)
   * 
   * Goal: Medium-dynamic data that changes occasionally but not constantly.
   * 
   * - staleTime: 30 seconds (data is fresh for 30s)
   * - gcTime: 5 minutes (keep in memory for 5min)
   * - refetchOnWindowFocus: true (user might manage settings in another tab)
   */
  mediumList: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
  } as const,

  /**
   * REAL-TIME WIDGETS (header counters: notifications, tasks, leads)
   * 
   * Goal: Small, cheap queries that should feel "live" (notification badges, task counters).
   * 
   * - staleTime: 10 seconds (data is fresh for 10s)
   * - gcTime: 60 seconds (keep in memory for 1 minute)
   * - refetchInterval: 15 seconds (poll every 15 seconds in background)
   * - refetchOnWindowFocus: true (refresh when user returns)
   */
  realtimeWidget: {
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 60 * 1000, // 60 seconds (1 minute)
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    refetchInterval: 15 * 1000, // 15 seconds - poll every 15 seconds
  } as const,

  /**
   * LEGACY: Static data (kept for backward compatibility)
   * @deprecated Use queryOptions.config instead
   */
  static: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  } as const,

  /**
   * LEGACY: Semi-static data (kept for backward compatibility)
   * @deprecated Use queryOptions.config instead
   */
  semiStatic: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  } as const,

  /**
   * LEGACY: Realtime data (kept for backward compatibility)
   * @deprecated Use queryOptions.realtimeWidget instead
   */
  realtime: {
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  } as const,

  /**
   * LEGACY: List data (kept for backward compatibility)
   * @deprecated Use queryOptions.heavyTable instead
   */
  list: (staleTimeSeconds: number = 30) => ({
    staleTime: staleTimeSeconds * 1000,
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    // NOTE: For smooth pagination, add placeholderData: keepPreviousData in individual queries
  }),

  /**
   * LEGACY: Dashboard analytics (kept for backward compatibility)
   * @deprecated Use queryOptions.dashboard instead
   */
  analytics: {
    staleTime: 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: 'ifStale',
    refetchOnWindowFocus: false,
  } as const,

  /**
   * DETAIL PAGES (client profile, lead detail, task detail, etc.)
   * 
   * Goal: Detail pages show specific entity data. Should be fresh when user navigates to them,
   * but cached when returning to the same detail page.
   * 
   * - staleTime: 1 minute (data is fresh for 1min)
   * - gcTime: 30 minutes (keep in memory for 30min)
   * - refetchOnWindowFocus: true (user expects detail to refresh when returning)
   * - refetchOnMount: 'ifStale' (only refetch if stale)
   * 
   * Use this for:
   * - Client profile pages (client-by-id queries)
   * - Lead detail pages
   * - Task detail pages
   * - Notes list on profile pages
   * - Task metadata on profile pages
   */
  detailPage: {
    staleTime: 60 * 1000, // 1 minute
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: true, // User expects detail to refresh when returning
    refetchOnReconnect: true,
    refetchOnMount: true, // Refetch on mount, but only if data is stale (older than staleTime) - this is the default behavior in v5
  } as const,

  /**
   * CALENDAR PAGE (events, tasks, slots, Google calendar)
   * 
   * Goal: Calendar is a heavy page that loads lots of data (events, slots, tasks, reminders).
   * Need to avoid re-fetching everything on every navigation between days/weeks/months.
   * Keep UX smooth while changing dates and views.
   * 
   * - staleTime: 1 minute (calendar can change but not every second)
   * - gcTime: 60 minutes (keep in memory for 1 hour)
   * - refetchOnWindowFocus: true (when user returns to tab, refresh events)
   * - refetchOnReconnect: true (refresh after reconnect)
   * - placeholderData: keepPreviousData (smooth transitions when changing dates - set per-query)
   * 
   * Use this for:
   * - Calendar events queries
   * - Calendar tasks queries
   * - Calendar slots queries
   * - Google calendar events queries
   */
  calendarPage: {
    staleTime: 60 * 1000, // 1 minute - calendar can change but not every second
    gcTime: 60 * 60 * 1000, // 60 minutes cache in memory
    refetchOnWindowFocus: true, // when user returns to tab, refresh events
    refetchOnReconnect: true, // refresh after reconnect
    refetchOnMount: true, // Refetch on mount, but only if data is stale (older than staleTime)
    // NOTE: For smooth date transitions, add placeholderData: keepPreviousData in individual queries
  } as const,
};


